import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';
import { resolveRiqueRouteContext } from '@/lib/riqueContext';
import { fetchRiqueAlerts, fetchRiqueBriefing } from '@/lib/riqueApi';
import { useAuthStore } from '@/stores/authStore';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import { useRiqueChat } from '@/hooks/useRiqueChat';
import { RiqueChatPanel } from '@/components/Rique/RiqueChatPanel';
import { RiqueEmailDialog } from '@/components/Rique/RiqueEmailDialog';
import { UserRole } from '@/types';

export const FloatingRiqueChat: React.FC = () => {
  const { user } = useAuthStore();
  const location = useLocation();
  const isGestorOrAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.GESTOR;
  const routeCtx = resolveRiqueRouteContext(location.pathname, location.search);

  const isOpen = useRiqueChatStore((s) => s.isOpen);
  const isMinimized = useRiqueChatStore((s) => s.isMinimized);
  const isExpanded = useRiqueChatStore((s) => s.isExpanded);
  const showHistory = useRiqueChatStore((s) => s.showHistory);
  const companyId = useRiqueChatStore((s) => s.companyId);
  const toggleRique = useRiqueChatStore((s) => s.toggleRique);
  const closeRique = useRiqueChatStore((s) => s.closeRique);
  const toggleMinimize = useRiqueChatStore((s) => s.toggleMinimize);
  const toggleExpanded = useRiqueChatStore((s) => s.toggleExpanded);
  const setShowHistory = useRiqueChatStore((s) => s.setShowHistory);
  const setCompanyId = useRiqueChatStore((s) => s.setCompanyId);

  const [emailOpen, setEmailOpen] = React.useState(false);

  const {
    companies,
    conversations,
    activeId,
    setActiveId,
    activeConversation,
    messages,
    streaming,
    loadingReply,
    lastAssistant,
    unavailable,
    handleSend,
    handleNewConversation,
    handleDeleteConversation,
    handleDeleteAllConversations,
  } = useRiqueChat(isGestorOrAdmin);

  const { data: alertsData } = useQuery({
    queryKey: ['rique-alerts', companyId],
    queryFn: () => fetchRiqueAlerts(companyId || undefined),
    enabled: isGestorOrAdmin,
    staleTime: 120_000,
  });

  const { data: briefingData } = useQuery({
    queryKey: ['rique-briefing', companyId],
    queryFn: () => fetchRiqueBriefing(companyId || undefined),
    enabled: isGestorOrAdmin && isOpen,
    staleTime: 300_000,
  });

  const alertCount = alertsData?.live?.length ?? 0;
  const briefing = briefingData?.live ?? briefingData?.cached;
  const selectedCompanyName = companies?.find((c) => c.id === companyId)?.name;

  if (!isGestorOrAdmin) return null;

  const isPortalPage = location.pathname === '/portal';
  const anchorClass = isPortalPage ? 'bottom-20 right-5 sm:right-8' : 'bottom-6 right-5 sm:right-8';
  const panelAnchorClass = isPortalPage ? 'bottom-[7.75rem] right-5 sm:right-8' : 'bottom-24 right-5 sm:right-8';

  const panelProps = {
    companies,
    companyId,
    setCompanyId,
    showHistory,
    setShowHistory,
    conversations,
    activeId,
    setActiveId,
    activeConversation,
    messages,
    streaming,
    loadingReply,
    unavailable,
    alerts: alertsData?.live,
    briefing,
    routeSuggestions: routeCtx.suggestions,
    lastAssistantText: lastAssistant,
    onSend: handleSend,
    onNewConversation: handleNewConversation,
    onDeleteConversation: handleDeleteConversation,
    onDeleteAllConversations: handleDeleteAllConversations,
    onClose: closeRique,
    onEmailClick: () => setEmailOpen(true),
    userName: user?.name ?? user?.email,
    companyName: selectedCompanyName,
  };

  return createPortal(
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={toggleRique}
          className={cn(
            'fixed z-[9999] flex h-14 items-center gap-2 rounded-full px-4 sm:px-5',
            anchorClass,
            'bg-primary text-primary-foreground shadow-xl shadow-primary/30 ring-2 ring-primary/20 ring-offset-2 ring-offset-background',
            'transition-transform hover:scale-[1.03] hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          )}
          title="Rique.Ia — analista"
          aria-label="Abrir Rique.Ia"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/95 p-0.5">
            <RiqueIcon className="h-full w-full" />
          </span>
          <span className="text-sm font-semibold leading-none">Rique.Ia</span>
          {alertCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {alertCount > 9 ? '9+' : alertCount}
            </span>
          ) : null}
        </button>
      )}

      {isOpen && isMinimized && !isExpanded && (
        <div
          className={cn(
            'fixed z-[9999] flex items-center gap-2 rounded-full border border-border/80',
            anchorClass,
            'bg-card px-4 py-2 shadow-xl'
          )}
        >
          <RiqueIcon className="h-5 w-5 shrink-0" />
          <span className="max-w-[10rem] truncate text-sm font-medium">Rique.Ia</span>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={toggleMinimize} aria-label="Expandir">
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={closeRique} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {isOpen && !isMinimized && !isExpanded && (
        <div
          className={cn(
            'fixed z-[9999] flex flex-col overflow-hidden',
            panelAnchorClass,
            'h-[min(620px,calc(100vh-6rem))] w-[min(420px,calc(100vw-2.5rem))]'
          )}
        >
          <RiqueChatPanel
            {...panelProps}
            onMinimize={toggleMinimize}
            onToggleExpand={toggleExpanded}
          />
        </div>
      )}

      {isOpen && isExpanded && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]" onClick={toggleExpanded} aria-hidden />
          <div className="relative flex h-[min(90vh,820px)] w-[min(960px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-border/80 bg-card shadow-2xl">
            <RiqueChatPanel {...panelProps} expanded onToggleExpand={toggleExpanded} />
          </div>
        </div>
      )}

      <RiqueEmailDialog
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        conversationId={activeId}
        companyId={companyId || undefined}
        defaultEmail={user?.email ?? ''}
      />
    </>,
    document.body
  );
};
